import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const QUESTIONS_PER_SLIDE = 3

export default class extends Controller {
  static targets = ["questionsContainer", "question", "optionsContainer", "option"]
  static values = {
    maxOptions: { type: Number, default: 6 },
    questionFontClass: { type: String, default: "font-oswald" },
    questionSizeClass: { type: String, default: "text-lg md:text-2xl" },
    questionWeightClass: { type: String, default: "font-bold" },
    contentFontClass: { type: String, default: "font-cardo" },
  }

  declare readonly questionsContainerTarget: HTMLElement
  declare questionTargets: HTMLElement[]
  declare readonly optionsContainerTargets: HTMLElement[]
  declare readonly optionTargets: HTMLElement[]
  declare maxOptionsValue: number
  declare questionFontClassValue: string
  declare questionSizeClassValue: string
  declare questionWeightClassValue: string
  declare contentFontClassValue: string

  private formElement: HTMLFormElement | null = null
  private nextQuestionIndex = 0
  private isSaving = false
  private formDirty = false
  private boundSaveOnSlideChange = this.saveOnSlideChange.bind(this)
  private boundSaveBeforeNavigate = this.saveBeforeNavigate.bind(this)

  connect() {
    this.formElement = this.element.closest("form") as HTMLFormElement | null
    this.formDirty = false
    this.initializeNextQuestionIndex()
    document.addEventListener("book-slider:savePieVoting", this.boundSaveOnSlideChange)
    document.addEventListener("saveBeforeNavigate", this.boundSaveBeforeNavigate)
    this.tryAutoAddQuestionOnLoad()
  }

  private tryAutoAddQuestionOnLoad() {
    if ((this.element as HTMLElement).dataset.pieVotingAutoAddQuestion === "1") return

    // Only auto-open when the server rendered the "Add another question" affordance (last chunk).
    const addWrapper = this.element.querySelector(".pie-add-question-wrapper") as HTMLElement | null
    if (!addWrapper) return

    const delays = [0, 80, 180]
    for (const delay of delays) {
      window.setTimeout(() => {
        if ((this.element as HTMLElement).dataset.pieVotingAutoAddQuestion === "1") return
        this.autoAddQuestionRowIfPossible()
      }, delay)
    }
  }

  private autoAddQuestionRowIfPossible() {
    if ((this.element as HTMLElement).dataset.pieVotingAutoAddQuestion === "1") return

    const container = this.questionsContainerTarget
    if (!container) return

    const addWrapper = this.element.querySelector(".pie-add-question-wrapper") as HTMLElement | null
    if (!addWrapper) return

    // If we already inserted a blank row, just focus its question input.
    const existingNewQuestion = container.querySelector(
      ".pie-question[data-question-id^='new_']",
    ) as HTMLElement | null
    if (existingNewQuestion) {
      ;(this.element as HTMLElement).dataset.pieVotingAutoAddQuestion = "1"
      return
    }

    const visibleCount = this.getVisibleQuestionCount()
    if (visibleCount >= QUESTIONS_PER_SLIDE) return

    const index = this.nextQuestionIndex++

    const questionTypographyClasses = this.getQuestionTypographyClasses()
    const questionHtml = `
      <div class="pie-question w-full space-y-4 p-4" data-pie-voting-target="question" data-question-id="new_${index}">
        <input type="hidden" name="book[pie_voting_questions_attributes][${index}][_destroy]" value="0" class="destroy-field">
        <input type="hidden" name="book[pie_voting_questions_attributes][${index}][include_in_book]" value="1">

        <div class="flex items-center justify-center gap-1 pie-question-text mb-4" data-mode="edit">
          <span class="question-display pie-question-display hidden ${questionTypographyClasses} text-center" data-action="click->pie-voting#questionDisplayClick"> </span>
          <input
            type="text"
            name="book[pie_voting_questions_attributes][${index}][question_text]"
            class="question-input pie-question-input ${questionTypographyClasses} text-center"
            placeholder="Type your question here"
            data-action="blur->pie-voting#questionChanged input->pie-voting#markDirty"
          />
          <button
            type="button"
            class="pie-question-remove-btn"
            data-action="pie-voting#removeQuestion"
            aria-label="Remove question"
          >
            <i class="bi bi-trash3 text-sm md:text-base"></i>
          </button>
        </div>

        <div
          class="w-full"
          data-pie-voting-target="optionsContainer"
          data-question-index="${index}"
          data-action="click->pie-voting#focusOptionInput"
        >
          <ul class="list-none pl-0 ml-0 border-gray-100 rounded-lg p-4 min-h-[72px] flex flex-row flex-wrap gap-2 md:gap-3 items-start ${this.contentFontClassValue || "font-cardo"}" style="border: 1px solid; list-style: none;">
            <li class="option-input-wrapper list-none pl-0 ml-0 w-full" style="display: none;">
              <input
                type="text"
                class="option-composer-input text-left border-0 outline-none p-1.5 w-full bg-transparent text-sm md:text-base"
                placeholder="Option"
                maxlength="30"
                data-action="keydown->pie-voting#handleOptionKey blur->pie-voting#handleOptionBlur click->pie-voting#stopPropagation"
              />
            </li>
          </ul>
        </div>
      </div>
    `

    // Insert before the Add button so it stays under the last question.
    if (addWrapper) {
      addWrapper.insertAdjacentHTML("beforebegin", questionHtml)
    } else {
      container.insertAdjacentHTML("beforeend", questionHtml)
    }

    ;(this.element as HTMLElement).dataset.pieVotingAutoAddQuestion = "1"
  }

  disconnect() {
    document.removeEventListener("book-slider:savePieVoting", this.boundSaveOnSlideChange)
    document.removeEventListener("saveBeforeNavigate", this.boundSaveBeforeNavigate)
  }

  private saveBeforeNavigate(event: Event) {
    const customEvent = event as CustomEvent<{ promises: Promise<unknown>[] }>
    if (!customEvent.detail?.promises) return
    if (!this.formDirty) return
    if (!this.hasAtLeastOneQuestion() && !this.hasPendingDestroy()) return
    if (this.formElement) {
      customEvent.detail.promises.push(this.saveForm({ userTriggered: false }))
    }
  }

  markDirty() {
    this.formDirty = true
  }

  private saveOnSlideChange(event: Event) {
    const customEvent = event as CustomEvent<{ leftSlide?: HTMLElement }>
    const leftSlide = customEvent.detail?.leftSlide
    if (leftSlide && !this.isElementInSlide(leftSlide)) return
    if (!this.formDirty) return
    if (!this.hasAtLeastOneQuestion() && !this.hasPendingDestroy()) return
    this.saveForm({ userTriggered: false })
  }

  private isElementInSlide(slideEl: HTMLElement): boolean {
    let node: Node | null = this.element
    while (node) {
      if (node === slideEl) return true
      const root = node.getRootNode() as ShadowRoot
      node = node.parentNode ?? root?.host ?? null
    }
    return false
  }

  private hasPendingDestroy(): boolean {
    const form = this.formElement
    if (!form) return false
    const container = form.querySelector(".pie-voting-pending-destroys")
    return container != null && container.querySelectorAll("input[name]").length > 0
  }

  private hasAtLeastOneQuestion(): boolean {
    const questions = this.formElement?.querySelectorAll(".pie-question") ?? []
    for (const q of questions) {
      if ((q as HTMLElement).style.display === "none") continue
      const questionInput = q.querySelector('input[name*="[question_text]"]') as HTMLInputElement
      if (questionInput?.value?.trim()) return true
      const optionInputs = q.querySelectorAll('input[name*="[option_text]"]')
      for (const opt of optionInputs) {
        if ((opt as HTMLInputElement).value?.trim()) return true
      }
    }
    return false
  }

  private getSliderElement(): HTMLElement | null {
    let node: Node | null = this.element
    while (node) {
      if (node instanceof HTMLElement && node.dataset?.controller?.includes?.("book-slider"))
        return node
      if (node instanceof ShadowRoot) {
        node = node.host
      } else {
        const root = node.getRootNode?.() as ShadowRoot | undefined
        node = node.parentNode ?? root?.host ?? null
      }
    }
    return null
  }

  /** Traverse up from this.element (through shadow boundaries) to find the div with data-slide-type^="pie-voting". */
  private getOurPieSlideWrapper(): HTMLElement | null {
    let node: Node | null = this.element
    while (node) {
      if (
        node instanceof HTMLElement &&
        node.getAttribute?.("data-slide-type")?.startsWith?.("pie-voting")
      )
        return node
      if (node instanceof ShadowRoot) {
        node = node.host
      } else {
        node = node.parentNode
      }
    }
    return null
  }

  private isOnLastPieVotingSlide(): boolean {
    const wrapper = this.getOurPieSlideWrapper()
    if (!wrapper) return false
    const ourSlide = wrapper.closest(".slick-slide")
    if (!ourSlide) return false
    const slider = this.getSliderElement()
    if (!slider) return false
    const slides = slider.querySelectorAll(".slick-slide")
    let lastPieSlide: Element | null = null
    for (const slide of slides) {
      if (slide.querySelector('[data-slide-type^="pie-voting"]')) lastPieSlide = slide
    }
    return lastPieSlide !== null && ourSlide === lastPieSlide
  }

  private showExpandLoader(): HTMLElement {
    // Use controller element (visible form area) so loader is always visible regardless of shadow DOM / slide structure
    const container = this.element as HTMLElement
    if (!container.style.position || container.style.position === "static") {
      container.style.position = "relative"
    }
    const loaderEl = document.createElement("div")
    loaderEl.className = "pie-voting-expand-loader"
    loaderEl.setAttribute("aria-live", "polite")
    loaderEl.setAttribute("role", "status")
    loaderEl.style.cssText =
      "position:absolute;inset:0;background:rgba(255,255,255,0.95);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;z-index:9999;border-radius:8px;"
    loaderEl.innerHTML = `
      <div class="spinner-border text-primary" role="status" style="width:2.5rem;height:2.5rem;">
        <span class="visually-hidden">Loading</span>
      </div>
      <p class="mb-0 small fw-medium text-secondary text-center px-3">Saving your questions and adding a new page so you can add more.</p>
    `
    container.appendChild(loaderEl)
    return loaderEl
  }

  private preserveDestroyForOption(optionEl: HTMLElement, optionId: string) {
    const form = this.formElement
    if (!form) return
    const optionInput = optionEl.querySelector('input[name*="[option_text]"]') as HTMLInputElement
    if (!optionInput) return
    const nameMatch = optionInput.name.match(
      /\[pie_voting_questions_attributes\]\[(\d+)\]\[pie_voting_options_attributes\]\[(\d+)\]/,
    )
    if (!nameMatch) return
    const [, qIndex, oIndex] = nameMatch

    let container = form.querySelector(".pie-voting-pending-destroys") as HTMLElement
    if (!container) {
      container = document.createElement("div")
      container.className = "pie-voting-pending-destroys"
      container.setAttribute("aria-hidden", "true")
      container.style.display = "none"
      form.appendChild(container)
    }

    const idInput = document.createElement("input")
    idInput.type = "hidden"
    idInput.name = `book[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][id]`
    idInput.value = optionId
    container.appendChild(idInput)

    const destroyInput = document.createElement("input")
    destroyInput.type = "hidden"
    destroyInput.name = `book[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][_destroy]`
    destroyInput.value = "1"
    container.appendChild(destroyInput)
  }

  private preserveDestroyForQuestion(questionEl: HTMLElement, questionId: string) {
    const form = this.formElement
    if (!form) return
    const questionInput = questionEl.querySelector(
      'input[name*="[question_text]"]',
    ) as HTMLInputElement | null
    if (!questionInput) return
    const nameMatch = questionInput.name.match(/\[pie_voting_questions_attributes\]\[(\d+)\]/)
    if (!nameMatch) return
    const [, qIndex] = nameMatch

    let container = form.querySelector(".pie-voting-pending-destroys") as HTMLElement | null
    if (!container) {
      container = document.createElement("div")
      container.className = "pie-voting-pending-destroys"
      container.setAttribute("aria-hidden", "true")
      container.style.display = "none"
      form.appendChild(container)
    }

    const idInput = document.createElement("input")
    idInput.type = "hidden"
    idInput.name = `book[pie_voting_questions_attributes][${qIndex}][id]`
    idInput.value = questionId
    container.appendChild(idInput)

    const destroyInput = document.createElement("input")
    destroyInput.type = "hidden"
    destroyInput.name = `book[pie_voting_questions_attributes][${qIndex}][_destroy]`
    destroyInput.value = "1"
    container.appendChild(destroyInput)
  }

  questionChanged(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const value = input.value.trim()
    if (!value) return
    const wrapper = input.closest(".pie-question-text") as HTMLElement | null
    if (wrapper) {
      const display = wrapper.querySelector(".question-display") as HTMLElement | null
      if (display) {
        display.textContent = value
        display.classList.remove("hidden")
        input.classList.add("hidden")
        wrapper.dataset.mode = "view"
      }
    }
    this.formDirty = true
  }

  questionDisplayClick(event: Event) {
    const span = event.currentTarget as HTMLElement
    const wrapper = span.closest(".pie-question-text") as HTMLElement | null
    if (!wrapper || wrapper.dataset.mode !== "view") return
    const input = wrapper.querySelector(".question-input") as HTMLInputElement | null
    if (!input) return
    span.classList.add("hidden")
    input.classList.remove("hidden")
    wrapper.dataset.mode = "edit"
    window.setTimeout(() => input.focus(), 0)
  }

  optionChanged(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const value = input.value.trim()
    if (!value) return
    const option = input.closest(".pie-option") as HTMLElement | null
    if (option) {
      const display = option.querySelector(".option-display") as HTMLElement | null
      if (display) {
        display.textContent = value
        display.classList.remove("hidden")
        input.classList.add("hidden")
        option.dataset.mode = "view"
      }
    }
    this.formDirty = true
  }

  optionDisplayClick(event: Event) {
    const span = event.currentTarget as HTMLElement
    const option = span.closest(".pie-option") as HTMLElement | null
    if (!option || option.dataset.mode !== "view") return
    const input = option.querySelector(".option-input") as HTMLInputElement | null
    if (!input) return
    span.classList.add("hidden")
    input.classList.remove("hidden")
    option.dataset.mode = "edit"
    window.setTimeout(() => input.focus(), 0)
  }

  stopPropagation(event: Event) {
    event.stopPropagation()
  }

  focusOptionInput(event: Event) {
    const container = event.currentTarget as HTMLElement | null
    if (!container?.matches("[data-pie-voting-target='optionsContainer']")) return
    const count = this.getOptionCountInContainer(container)
    if (count >= this.maxOptionsValue) return
    const wrapper = container.querySelector(".option-input-wrapper") as HTMLElement | null
    const input = container.querySelector(".option-composer-input") as HTMLInputElement | null
    if (wrapper && input) {
      wrapper.style.display = "flex"
      setTimeout(() => input.focus(), 10)
    }
  }

  handleOptionKey(event: KeyboardEvent) {
    if (event.key !== "Enter") return
    const input = event.currentTarget as HTMLInputElement
    const value = input.value.trim()
    if (!value) return
    event.preventDefault()
    const container = input.closest(
      "[data-pie-voting-target='optionsContainer']",
    ) as HTMLElement | null
    if (!container) return
    this.addOptionBadge(container, value)
    input.value = ""
    setTimeout(() => input.focus(), 10)
  }

  handleOptionBlur(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const value = input.value.trim()
    const container = input.closest(
      "[data-pie-voting-target='optionsContainer']",
    ) as HTMLElement | null
    if (value && container) {
      this.addOptionBadge(container, value)
      input.value = ""
    }
    const wrapper = input.closest(".option-input-wrapper") as HTMLElement | null
    if (wrapper) wrapper.style.display = "none"
  }

  private addOptionBadge(container: HTMLElement, text: string) {
    const trimmed = text.trim().slice(0, 40)
    if (!trimmed) return
    const questionIndexAttr = container.getAttribute("data-question-index")
    if (questionIndexAttr == null) return
    const questionIndex = parseInt(questionIndexAttr, 10)
    if (Number.isNaN(questionIndex)) return
    const count = this.getOptionCountInContainer(container)
    if (count >= this.maxOptionsValue) return
    const existingTexts = Array.from(container.querySelectorAll(".pie-option-tag-item span.flex-1"))
      .map((s) => s.textContent?.trim().toLowerCase())
      .filter(Boolean)
    if (existingTexts.includes(trimmed.toLowerCase())) return
    const usedIndices = new Set<number>()
    container.querySelectorAll(".pie-option input[name*='[option_text]']").forEach((el) => {
      const match = (el as HTMLInputElement).name.match(
        /\[pie_voting_questions_attributes\]\[\d+\]\[pie_voting_options_attributes\]\[(\d+)\]/,
      )
      if (match) usedIndices.add(parseInt(match[1], 10))
    })
    // Keep nested attribute indices unique even after deleting persisted options.
    // Pending destroys are stored outside the visible question container, so include
    // those indices too to prevent collisions that can drop newly added options.
    const form = this.formElement
    const pendingDestroys = form?.querySelector(".pie-voting-pending-destroys")
    if (pendingDestroys) {
      pendingDestroys
        .querySelectorAll("input[name*='[pie_voting_options_attributes]'][name$='[id]']")
        .forEach((el) => {
          const match = (el as HTMLInputElement).name.match(
            new RegExp(
              String.raw`\[pie_voting_questions_attributes\]\[${questionIndex}\]\[pie_voting_options_attributes\]\[(\d+)\]`,
            ),
          )
          if (match) usedIndices.add(parseInt(match[1], 10))
        })
    }
    let nextIndex = 0
    while (usedIndices.has(nextIndex)) nextIndex++
    const wrapperLi = container.querySelector(".option-input-wrapper")
    if (wrapperLi) {
      const template = document.createElement("template")
      template.innerHTML = `
        <li class="pie-option pie-option-tag-item p-1.5 rounded-lg flex items-center gap-2 text-sm md:text-base text-gray-800 font-medium list-none pl-0 ml-0 shrink-0" style="background: #EEECF6;" data-pie-voting-target="option">
          <input type="hidden" name="book[pie_voting_questions_attributes][${questionIndex}][pie_voting_options_attributes][${nextIndex}][_destroy]" value="0" class="destroy-field">
          <input type="hidden" name="book[pie_voting_questions_attributes][${questionIndex}][pie_voting_options_attributes][${nextIndex}][option_text]" class="option-text-field">
          <span class="flex-1"></span>
          <button type="button" class="p-0 m-0 bg-transparent border-0 cursor-pointer flex items-center justify-center text-gray-500 hover:text-gray-800 shrink-0" data-action="pie-voting#removeOption" aria-label="Remove option">
            <i class="bi bi-x-lg text-xs md:text-sm"></i>
          </button>
        </li>
      `

      const optionItem = template.content.firstElementChild as HTMLLIElement
      ;(optionItem.querySelector("input.option-text-field") as HTMLInputElement).value = trimmed
      optionItem.querySelector("span.flex-1").textContent = trimmed
      wrapperLi.before(optionItem)
    }
    this.formDirty = true
    if (this.getOptionCountInContainer(container) >= this.maxOptionsValue) {
      const w = container.querySelector(".option-input-wrapper") as HTMLElement | null
      if (w) w.style.display = "none"
    }
  }

  private getOptionCountInContainer(container: HTMLElement): number {
    return container.querySelectorAll(".pie-option.pie-option-tag-item").length
  }

  addQuestion() {
    if (!this.questionsContainerTarget || !this.formElement) return
    if (this.isSaving) return

    const visibleCount = this.getVisibleQuestionCount()
    const pageFull = visibleCount >= QUESTIONS_PER_SLIDE

    // Page full (3 questions): save, show loader, server re-renders with chunks so right page of same double gets empty form; we navigate there. Next additions (4th–6th) stay on right; 7th triggers expand again → new double page.
    if (pageFull) {
      this.formDirty = true
      ;(
        window as Window & { b4mGoToLastPieVotingAfterReload?: boolean }
      ).b4mGoToLastPieVotingAfterReload = true
      const loaderEl = this.showExpandLoader()
      this.saveForm({ expandPieVotingSlide: true, loaderElement: loaderEl })
      return
    }

    // Add a new question row to the current page (0 or 1 question so far)
    const index = this.nextQuestionIndex++

    const questionTypographyClasses = this.getQuestionTypographyClasses()
    const questionHtml = `
      <div class="pie-question w-full space-y-4 p-4" data-pie-voting-target="question" data-question-id="new_${index}">
        <input type="hidden" name="book[pie_voting_questions_attributes][${index}][_destroy]" value="0" class="destroy-field">
        <input type="hidden" name="book[pie_voting_questions_attributes][${index}][include_in_book]" value="1">

        <div class="flex items-center justify-center gap-1 pie-question-text mb-4" data-mode="edit">
          <span class="question-display pie-question-display hidden ${questionTypographyClasses} text-center" data-action="click->pie-voting#questionDisplayClick"> </span>
          <input
            type="text"
            name="book[pie_voting_questions_attributes][${index}][question_text]"
            class="question-input pie-question-input ${questionTypographyClasses} text-center"
            placeholder="Type your question here"
            data-action="blur->pie-voting#questionChanged input->pie-voting#markDirty"
          />
          <button
            type="button"
            class="pie-question-remove-btn"
            data-action="pie-voting#removeQuestion"
            aria-label="Remove question"
          >
            <i class="bi bi-trash3 text-sm md:text-base"></i>
          </button>
        </div>

        <div
          class="w-full"
          data-pie-voting-target="optionsContainer"
          data-question-index="${index}"
          data-action="click->pie-voting#focusOptionInput"
        >
          <ul class="list-none pl-0 ml-0 border-gray-100 rounded-lg p-4 min-h-[72px] flex flex-row flex-wrap gap-2 md:gap-3 items-start ${this.contentFontClassValue || "font-cardo"}" style="border: 1px solid; list-style: none;">
            <li class="option-input-wrapper list-none pl-0 ml-0 w-full" style="display: none;">
              <input
                type="text"
                class="option-composer-input text-left border-0 outline-none p-1.5 w-full bg-transparent text-sm md:text-base"
                placeholder="Option"
                maxlength="30"
                data-action="keydown->pie-voting#handleOptionKey blur->pie-voting#handleOptionBlur click->pie-voting#stopPropagation"
              />
            </li>
          </ul>
        </div>
      </div>
    `

    // Insert before the Add button so it stays under the last question
    const addBtnWrapper = this.questionsContainerTarget.querySelector(".pie-add-question-wrapper")
    if (addBtnWrapper) {
      addBtnWrapper.insertAdjacentHTML("beforebegin", questionHtml)
    } else {
      this.questionsContainerTarget.insertAdjacentHTML("beforeend", questionHtml)
    }
    this.formDirty = true
  }

  removeOption(event: Event) {
    const button = event.currentTarget as HTMLElement
    const option = button.closest(".pie-option") as HTMLElement | null
    if (!option) return

    const idField = option.querySelector('input[name*="[id]"]') as HTMLInputElement | null

    if (idField?.value) {
      this.preserveDestroyForOption(option, idField.value)
      option.remove()
    } else {
      option.remove()
    }
    this.formDirty = true
  }

  removeQuestion(event: Event) {
    const button = event.currentTarget as HTMLElement
    const question = button.closest(".pie-question") as HTMLElement | null
    if (!question) return

    const idField = question.querySelector(
      'input[name*="[pie_voting_questions_attributes]"][name$="[id]"]',
    ) as HTMLInputElement | null
    if (idField?.value) {
      this.preserveDestroyForQuestion(question, idField.value)
      question.remove()
    } else {
      question.remove()
    }

    this.formDirty = true
    this.initializeNextQuestionIndex()
  }

  private getVisibleQuestionCount(): number {
    const questions = this.formElement?.querySelectorAll(".pie-question") ?? []
    return Array.from(questions).filter((q) => (q as HTMLElement).style.display !== "none").length
  }

  private initializeNextQuestionIndex() {
    if (!this.formElement) return

    const inputs = this.formElement.querySelectorAll(
      'input[name^="book[pie_voting_questions_attributes]"]',
    ) as NodeListOf<HTMLInputElement>

    const indices = new Set<number>()
    inputs.forEach((input) => {
      const match = input.name.match(/\[pie_voting_questions_attributes\]\[(\d+)\]/)
      if (match) indices.add(parseInt(match[1], 10))
    })

    let next = 0
    while (indices.has(next)) next++
    this.nextQuestionIndex = next
  }

  private getQuestionTypographyClasses(): string {
    const size = this.questionSizeClassValue?.trim() || "text-lg md:text-2xl"
    const weight = this.questionWeightClassValue?.trim() || "font-bold"
    const font = this.questionFontClassValue?.trim() || "font-oswald"
    return `${size} ${weight} ${font}`
  }

  private buildFormDataFromControls(form: HTMLFormElement): FormData {
    const original = new FormData(form)
    const formData = new FormData()

    // Keep non-pie params untouched.
    for (const [key, value] of original.entries()) {
      if (!key.includes("[pie_voting_questions_attributes]")) {
        formData.append(key, value)
      }
    }

    const questions = Array.from(form.querySelectorAll(".pie-question")) as HTMLElement[]
    const questionNameRegex = /\[pie_voting_questions_attributes\]\[(\d+)\]/
    const optionNameRegex =
      /\[pie_voting_questions_attributes\]\[(\d+)\]\[pie_voting_options_attributes\]\[(\d+)\]/

    for (const questionEl of questions) {
      const questionInput = questionEl.querySelector(
        'input[name*="[question_text]"]',
      ) as HTMLInputElement | null
      if (!questionInput) continue

      const questionMatch = questionInput.name.match(questionNameRegex)
      if (!questionMatch) continue
      const qIndex = questionMatch[1]

      const questionIdInput = questionEl.querySelector(
        'input[name*="[pie_voting_questions_attributes]"][name$="[id]"]',
      ) as HTMLInputElement | null
      const questionDestroyInput = questionEl.querySelector(
        'input[name*="[pie_voting_questions_attributes]"][name$="[_destroy]"]',
      ) as HTMLInputElement | null
      const includeInBookInput = questionEl.querySelector(
        'input[name*="[include_in_book]"]',
      ) as HTMLInputElement | null

      const questionText = questionInput.value.trim()
      const questionId = questionIdInput?.value?.trim() ?? ""
      const questionDestroy = questionDestroyInput?.value ?? "0"
      const includeInBook = includeInBookInput?.value ?? "1"

      const optionEntries: Array<{
        optionId: string
        optionText: string
        optionDestroy: string
        oIndex: string
      }> = []

      const optionNodes = questionEl.querySelectorAll(
        '.pie-option input[name*="[pie_voting_options_attributes]"][name*="[option_text]"]',
      ) as NodeListOf<HTMLInputElement>

      optionNodes.forEach((optionTextInput) => {
        const optionMatch = optionTextInput.name.match(optionNameRegex)
        if (!optionMatch) return
        const oIndex = optionMatch[2]
        const optionText = optionTextInput.value.trim()

        const optionIdInput = questionEl.querySelector(
          `input[name*="[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][id]"]`,
        ) as HTMLInputElement | null
        const optionDestroyInput = questionEl.querySelector(
          `input[name*="[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][_destroy]"]`,
        ) as HTMLInputElement | null

        const optionId = optionIdInput?.value?.trim() ?? ""
        const optionDestroy = optionDestroyInput?.value ?? "0"
        optionEntries.push({ optionId, optionText, optionDestroy, oIndex })
      })

      const hasUsefulOption = optionEntries.some((opt) => opt.optionText.length > 0)
      const isNewBlankQuestion =
        !questionId && questionDestroy !== "1" && !questionText && !hasUsefulOption
      if (isNewBlankQuestion) continue

      if (questionId) {
        formData.append(`book[pie_voting_questions_attributes][${qIndex}][id]`, questionId)
      }
      formData.append(`book[pie_voting_questions_attributes][${qIndex}][_destroy]`, questionDestroy)
      formData.append(
        `book[pie_voting_questions_attributes][${qIndex}][include_in_book]`,
        includeInBook,
      )
      if (questionText.length > 0) {
        formData.append(
          `book[pie_voting_questions_attributes][${qIndex}][question_text]`,
          questionText,
        )
      }

      optionEntries.forEach(({ optionId, optionText, optionDestroy, oIndex }) => {
        const isBlankNewOption = !optionId && optionDestroy !== "1" && optionText.length === 0
        if (isBlankNewOption) return

        if (optionId) {
          formData.append(
            `book[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][id]`,
            optionId,
          )
        }

        if (optionText.length > 0) {
          formData.append(
            `book[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][option_text]`,
            optionText,
          )
          formData.append(
            `book[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][_destroy]`,
            "0",
          )
        } else if (optionId) {
          // Persisted empty options should be removed instead of being re-sent blank.
          formData.append(
            `book[pie_voting_questions_attributes][${qIndex}][pie_voting_options_attributes][${oIndex}][_destroy]`,
            "1",
          )
        }
      })
    }

    const pendingDestroys = form.querySelector(".pie-voting-pending-destroys")
    if (pendingDestroys) {
      const pendingInputs = pendingDestroys.querySelectorAll(
        "input[name]",
      ) as NodeListOf<HTMLInputElement>
      pendingInputs.forEach((input) => {
        formData.append(input.name, input.value)
      })
    }

    return formData
  }

  private saveForm(
    options: {
      userTriggered?: boolean
      expandPieVotingSlide?: boolean
      loaderElement?: HTMLElement | null
    } = {},
  ): Promise<void> {
    if (!this.formElement || this.isSaving) return Promise.resolve()
    if (!this.hasAtLeastOneQuestion() && !this.hasPendingDestroy()) return Promise.resolve()

    const expandPieVotingSlide = options.expandPieVotingSlide === true
    const loaderElement = options.loaderElement ?? null

    this.isSaving = true
    const formData = this.buildFormDataFromControls(this.formElement)
    if (expandPieVotingSlide) {
      formData.append("expand_pie_voting_slide", "1")
    }

    const form = this.formElement
    return fetch(form.action, {
      method: form.method || "PATCH",
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
      },
    })
      .then(async (res) => {
        const html = await res.text()
        if (res.ok) {
          if (html.includes("turbo-stream")) {
            Turbo.renderStreamMessage(html)
          }
          const pending = form.querySelector(".pie-voting-pending-destroys")
          pending?.replaceChildren()
        }
      })
      .catch((error) => {
        console.error("Error saving pie voting form", error)
      })
      .finally(() => {
        this.isSaving = false
        if (!expandPieVotingSlide) {
          this.formDirty = false
        }
        if (loaderElement?.parentNode) {
          loaderElement.remove()
        }
        this.initializeNextQuestionIndex()
      })
  }
}
